import React from 'react'
import { Button, Card, CardContent, CardHeader, Switch, Label } from '@/shared/ui'
import { getDiagnostics, showLocalTestNotification } from '@/modules/pwa/services/webpushClient'
import { useToast } from '@/shared/ui/Toast'
import { getWebpushEnabledForAll, setWebpushEnabledForAll } from '../services/pushSettingsService'
import { getPushPolicy, setPushPolicy, PushPolicy } from '../services/pushPolicyService'
import { Input } from '@/shared/ui/Input'
import { Switch as SwitchControl } from '@/shared/ui/SwitchControl'
import PlanPushPolicySettings from './PlanPushPolicySettings'

export default function PushConfigSettings() {
  const [diag, setDiag] = React.useState<{ pwaInstalled: boolean; permission: any; controller: boolean; swState: string | null; vapidOk: boolean } | null>(null)
  const { add: toast } = useToast()
  const [enabledAll, setEnabledAll] = React.useState<boolean>(false)
  const [loadingAll, setLoadingAll] = React.useState<boolean>(true)
  const [savingAll, setSavingAll] = React.useState<boolean>(false)
  const [policy, setPolicy] = React.useState<PushPolicy | null>(null)
  const [policyDirty, setPolicyDirty] = React.useState<boolean>(false)
  const [loadingPolicy, setLoadingPolicy] = React.useState<boolean>(true)
  const [savingPolicy, setSavingPolicy] = React.useState<boolean>(false)

  React.useEffect(()=>{
    let alive = true
    ;(async()=>{ try { const d = await getDiagnostics(); if (alive) setDiag(d) } catch {} })()
    const t = setInterval(async ()=>{ try { const d = await getDiagnostics(); if (alive) setDiag(d) } catch {} }, 3000)
    return ()=>{ alive=false; clearInterval(t) }
  },[])

  React.useEffect(()=>{
    let alive = true
    ;(async()=>{
      try { const p = await getPushPolicy(); if (alive) setPolicy(p) }
      catch { /* ignore */ }
      finally { if (alive) setLoadingPolicy(false) }
    })()
    return ()=>{ alive=false }
  },[])

  React.useEffect(()=>{
    let alive = true
    ;(async()=>{
      try { const v = await getWebpushEnabledForAll(); if (alive) setEnabledAll(v) }
      catch { /* ignore */ }
      finally { if (alive) setLoadingAll(false) }
    })()
    return ()=>{ alive = false }
  },[])

  async function onToggleAll(next:boolean){
    if (savingAll) return
    setSavingAll(true)
    try {
      await setWebpushEnabledForAll(next)
      setEnabledAll(next)
      toast({ title: 'Configurar Push', description: next ? 'Liberado para todos os usuários.' : 'Restrito ao master.', variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Configurar Push', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSavingAll(false)
    }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-medium">Configurar Push</h2>
      <p className="text-sm text-foreground/70">Diagnóstico e utilitários do PWA/Service Worker (somente master).</p>

      <Card>
        <CardHeader className="text-sm font-medium">Liberação geral</CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-foreground/80">
              Quando ativado, qualquer usuário poderá habilitar push no dispositivo. No MVP, mantenha desativado até finalizar os testes.
            </div>
            <div className="flex items-center gap-2">
              <Label>Restrito</Label>
              <Switch checked={enabledAll} onChange={(e)=> onToggleAll((e.target as HTMLInputElement).checked)} disabled={loadingAll || savingAll} />
              <Label>Liberado</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="text-sm font-medium">Política de envio</CardHeader>
        <CardContent>
          {policy ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-foreground/80">
                  Agrupar múltiplas oportunidades dentro da janela de silêncio.
                </div>
                <div className="flex items-center gap-2">
                  <Label>Individual</Label>
                  <SwitchControl checked={policy.aggregate} onChange={(e)=>{ const on=(e.target as HTMLInputElement).checked; setPolicy(p=> p?{...p, aggregate:on}:p); setPolicyDirty(true) }} />
                  <Label>Agrupar</Label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div className="col-span-1">
                  <Label className="block mb-1">Delay entre notificações (minutos)</Label>
                  <Input type="number" min={0} step={1} value={policy ? Math.round((policy.cooldownMs||0)/60000) : 0} onChange={(e)=>{ const mins = Math.max(0, Math.round(Number((e.target as HTMLInputElement).value)||0)); setPolicy(p=> p?{...p, cooldownMs: mins*60000}:p); setPolicyDirty(true) }} />
                </div>
                <div className="col-span-1">
                  <Label className="block mb-1">Mostrar resumo a partir de (qtd)</Label>
                  <Input type="number" min={1} step={1} value={policy?.aggregateMin ?? 3} onChange={(e)=>{ const n = Math.max(1, Math.round(Number((e.target as HTMLInputElement).value)||1)); setPolicy(p=> p?{...p, aggregateMin:n}:p); setPolicyDirty(true) }} />
                </div>
                <div className="col-span-1">
                  <Label className="block mb-1">Aguardar após publicar (seg)</Label>
                  <Input type="number" min={0} step={5} value={policy?.notifyGuardSec ?? 60} onChange={(e)=>{ const s = Math.max(0, Math.round(Number((e.target as HTMLInputElement).value)||0)); setPolicy(p=> p?{...p, notifyGuardSec:s}:p); setPolicyDirty(true) }} />
                </div>
                <div className="col-span-1 flex gap-2">
                  <Button disabled={savingPolicy || !policyDirty} onClick={async()=>{
                    if (!policy) return
                    setSavingPolicy(true)
                    try {
                      await setPushPolicy(policy)
                      setPolicyDirty(false)
                      toast({ title: 'Política de envio', description: 'Configurações salvas.', variant: 'success', soft: true })
                    } catch (e:any) {
                      toast({ title: 'Política de envio', description: e?.message || 'Falha ao salvar', variant: 'error' })
                    } finally {
                      setSavingPolicy(false)
                    }
                  }}>Salvar</Button>
                  <Button variant="outline" onClick={async()=>{
                    setLoadingPolicy(true)
                    try { const p = await getPushPolicy(); setPolicy(p); setPolicyDirty(false) } finally { setLoadingPolicy(false) }
                  }}>Recarregar</Button>
                </div>
              </div>
              <p className="text-xs text-foreground/60">Regra: após enviar um push, respeitamos o delay antes do próximo. Novas oportunidades dentro do período serão agrupadas em um único push de resumo quando o período encerrar (se atingir o limiar).</p>
            </div>
          ) : (
            <div className="text-sm text-foreground/70">{loadingPolicy ? 'Carregando…' : 'Não foi possível carregar a política.'}</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="text-sm font-medium">Diagnóstico</CardHeader>
        <CardContent>
          {diag ? (
            <div className="space-y-1 text-sm text-foreground/80">
              <div>• PWA instalado: {String(diag.pwaInstalled)}</div>
              <div>• Permissão: {String(diag.permission)}</div>
              <div>• SW state: {diag.swState || 'n/a'}; controller: {String(diag.controller)}</div>
              <div>• VAPID ok: {String(diag.vapidOk)}</div>
              {typeof (window as any).__SW_DIAG__ !== 'undefined' && (
                <div>• SW diag: {(window as any).__SW_DIAG__}</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-foreground/70">Carregando…</div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" className="h-11" onClick={async ()=>{
              const ok = await showLocalTestNotification();
              toast({ title: 'Teste local', description: ok ? 'Banner solicitado ao sistema.' : 'Falha ao solicitar banner.', variant: ok ? 'success' : 'error', soft: true })
            }}>Testar notificação local</Button>
            <Button variant="outline" className="h-11" onClick={()=> location.reload()}>Recarregar app</Button>
          </div>
        </CardContent>
      </Card>
      <PlanPushPolicySettings />
    </div>
  )
}
